import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { I18n, useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarNoCarDepency } from "../../http/cars/types";
import { useAdminMileagesApi, useMyCarMileagesApi } from "../../http/mileages/api";
import { CheckMileageBody, ConflictMileage, Mileage } from "../../http/mileages/types";
import { convertKilometersToMiles, convertMilesToKilometers, formatDateNumeric, formatNumber } from "../../types/utils";
import { ErrorBox } from "../ui/Boxes";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Inline from "../ui/Inline";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

export interface MileageFormBody {
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  } | null;
  date: string;
  distance: number;
}

type MileageFormProps = {
  mileage?: Mileage | null;
  onSubmit: (form: MileageFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
  carDriverId?: string;
};

type ConflictMileageWithDate = ConflictMileage & {
  date: string;
  distance: number;
};

const MileageForm: FC<MileageFormProps> = ({
  mileage,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
  carDriverId,
}: MileageFormProps) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carsApi = useAdminCarsApi();
  const aborter = useRef(new AbortController());
  const mileagesApi = useMyCarMileagesApi();
  const mileagesAdminApi = useAdminMileagesApi();
  const dispatch = useContext(NotificationDispatch);

  const [conflict, setConflict] = useState<ConflictMileageWithDate>();
  const [lastRecord, setLastRecord] = useState<{
    date: string;
    distance: number;
  }>();

  const distanceInMiles = !!userSettings.me.distanceInMiles;

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const defaultValues = {
    car: mileage?.car,
    date: mileage?.date,
    distance: mileage?.distance && distanceInMiles ? convertKilometersToMiles(mileage.distance) : mileage?.distance,
  };

  const carID = carId ?? mileage?.car.id;

  const loadLastMileage = async (carId: string, carDriverId?: string) => {
    try {
      const lastMileage = carDriverId
        ? await mileagesApi.getLastMileage(aborter.current.signal, carId, carDriverId)
        : await mileagesAdminApi.getLastMileage(aborter.current.signal, carId);
      setLastRecord(lastMileage);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  useEffect(() => {
    carID && loadLastMileage(carID, carDriverId);
  }, [carID]);

  const handleSubmit = async (formData: MileageFormBody) => {
    setConflict(undefined);
    try {
      const body: CheckMileageBody = {
        date: formData.date,
        distance: distanceInMiles ? convertMilesToKilometers(formData.distance) : formData.distance,
        carId: carID || formData.car?.id || "",
        mileageId: mileage?.id,
      };

      const currentConflict = carDriverId
        ? await mileagesApi.getConflictMileages(body, carId!)
        : await mileagesAdminApi.getAdminConflictMileages(body);

      setConflict({ ...currentConflict, date: body.date, distance: body.distance });

      const hasConflictData =
        currentConflict?.minDate ||
        currentConflict?.maxDate ||
        currentConflict?.prevRecord ||
        currentConflict?.nextRecord;

      if (!hasConflictData) {
        await onSubmit(formData);
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <>
      <Form<MileageFormBody>
        defaultValues={defaultValues}
        submitText={i18n.t.common.save}
        disabled={isDisabled}
        onSubmit={() => handleSubmit}
        itemCreationInfo={mileage}
      >
        {({ control, reset, setValue, watch }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [mileage]);

          return (
            <FormFieldsContainer>
              <Controller
                name="car"
                control={control}
                rules={{ required: !carId }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error} hidden={!!carId}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          onChange={(value) => {
                            value ? loadLastMileage(value.id, carDriverId) : setLastRecord(undefined);
                            field.onChange(value);
                          }}
                          isDisabled={isDisabled || isLoading}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<{
                            id: string;
                            vehicleIdentificationNumber: string;
                            currentPlateNumber?: string;
                            lastPlateNumber?: string;
                          }>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.common.lastRecord} isOptional>
                <Inline>
                  <Input disabled value={lastRecord ? formatDateNumeric(lastRecord?.date, i18n.language) : ""} />
                  <Numeric
                    disabled
                    value={
                      lastRecord?.distance
                        ? distanceInMiles
                          ? convertKilometersToMiles(lastRecord?.distance ?? 0)
                          : lastRecord?.distance
                        : ""
                    }
                    suffix={distanceInMiles ? ` ${i18n.t.common.miles}` : " km"}
                  />
                </Inline>
              </FormField>

              <Controller
                name="distance"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.distance} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        defaultValue={mileage?.distance}
                        onBlur={(value) => value !== null && setValue("distance", value)}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading}
                        suffix={distanceInMiles ? ` ${i18n.t.common.miles}` : " km"}
                        decimalScale={0}
                      />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="date"
                control={control}
                rules={{ required: true }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.date} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => !!v && setValue("date", v)}
                        value={watch("date")}
                        disabled={isDisabled || isLoading}
                        isInvalid={isInvalid}
                      />
                    )}
                  </FormField>
                )}
              />
              {!!conflict && renderConflictMessage(conflict, i18n, distanceInMiles)}
            </FormFieldsContainer>
          );
        }}
      </Form>
    </>
  );
};

export default MileageForm;

const renderConflictMessage = (conflict: ConflictMileageWithDate, i18n: I18n, distanceInMiles: boolean) => {
  if (conflict.prevRecord && conflict.nextRecord) {
    return (
      <ErrorBox
        text={i18n.t.mileages.errors.mileageMustBeBetween(
          formatDateNumeric(conflict.prevRecord.date, i18n.language),
          formatNumber(
            !!distanceInMiles ? convertKilometersToMiles(conflict.prevRecord.distance) : conflict.prevRecord.distance,
            i18n.language,
            {
              showThousendSeparator: true,
              fractionalDigits: 0,
            },
          ) + (!!distanceInMiles ? ` ${i18n.t.common.miles}` : " km"),
          formatDateNumeric(conflict.nextRecord.date, i18n.language),
          formatNumber(
            !!distanceInMiles ? convertKilometersToMiles(conflict.nextRecord.distance) : conflict.nextRecord.distance,
            i18n.language,
            {
              showThousendSeparator: true,
              fractionalDigits: 0,
            },
          ) + (!!distanceInMiles ? ` ${i18n.t.common.miles}` : " km"),
        )}
      />
    );
  } else if (conflict.prevRecord) {
    return (
      <ErrorBox
        text={i18n.t.mileages.errors.mileageMustBeGreater(
          formatNumber(
            !!distanceInMiles ? convertKilometersToMiles(conflict.prevRecord.distance) : conflict.prevRecord.distance,
            i18n.language,
            {
              showThousendSeparator: true,
              fractionalDigits: 0,
            },
          ) + (!!distanceInMiles ? ` ${i18n.t.common.miles}` : " km"),
          formatDateNumeric(conflict.prevRecord.date, i18n.language),
        )}
      />
    );
  } else if (conflict.nextRecord) {
    return (
      <ErrorBox
        text={i18n.t.mileages.errors.mileageMustBeLower(
          formatNumber(
            !!distanceInMiles ? convertKilometersToMiles(conflict.nextRecord.distance) : conflict.nextRecord.distance,
            i18n.language,
            {
              showThousendSeparator: true,
              fractionalDigits: 0,
            },
          ) + (!!distanceInMiles ? ` ${i18n.t.common.miles}` : " km"),
          formatDateNumeric(conflict.nextRecord.date, i18n.language),
        )}
      />
    );
  }

  return <></>;
};
